import {
  ChevronRight,
  Folder,
  MoreHorizontal,
  Share,
  Trash2,
  type LucideIcon,
} from "lucide-react"
import { useState } from "react"

import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import {
  SidebarGroup,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuAction,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  useSidebar,
} from "@/components/ui/sidebar"
import { Link } from "react-router-dom"

interface NavSubItem {
  title: string
  url: string
  items?: { title: string; url: string }[]
}

interface NavAgent {
  name: string
  url: string
  icon: LucideIcon
  items?: NavSubItem[]
}

function NavSubItemWithChildren({ subItem }: { subItem: NavSubItem }) {
  const [isOpen, setIsOpen] = useState(true)

  if (!subItem.items || subItem.items.length === 0) {
    return (
      <SidebarMenuSubItem>
        <SidebarMenuSubButton asChild>
          <Link to={subItem.url}>
            <span>{subItem.title}</span>
          </Link>
        </SidebarMenuSubButton>
      </SidebarMenuSubItem>
    )
  }

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen} asChild>
      <SidebarMenuSubItem>
        <div className="flex items-center w-full">
          <SidebarMenuSubButton asChild className="flex-1">
            <Link to={subItem.url}>
              <span>{subItem.title}</span>
            </Link>
          </SidebarMenuSubButton>
          <CollapsibleTrigger asChild>
            <button
              className="p-1 hover:bg-sidebar-accent rounded-sm mr-2"
              onClick={(e) => e.stopPropagation()}
            >
              <ChevronRight className={`h-4 w-4 transition-transform duration-200 ${isOpen ? 'rotate-90' : ''}`} />
            </button>
          </CollapsibleTrigger>
        </div>
        <CollapsibleContent>
          <div className="ml-4 border-l border-sidebar-border pl-2 mt-1">
            {subItem.items.map((nestedItem) => (
              <SidebarMenuSubItem key={nestedItem.title}>
                <SidebarMenuSubButton asChild>
                  <Link to={nestedItem.url}>
                    <span>{nestedItem.title}</span>
                  </Link>
                </SidebarMenuSubButton>
              </SidebarMenuSubItem>
            ))}
          </div>
        </CollapsibleContent>
      </SidebarMenuSubItem>
    </Collapsible>
  )
}

export function NavAgents({
  agents,
}: {
  agents: NavAgent[]
}) {
  const { isMobile } = useSidebar()

  return (
    <SidebarGroup className="group-data-[collapsible=icon]:hidden">
      <SidebarGroupLabel>Agents</SidebarGroupLabel>
      <SidebarMenu>
        {agents.map((item) => (
          <Collapsible
            key={item.name}
            asChild
            defaultOpen={item.items ? true : false}
            className="group/collapsible"
          >
            <SidebarMenuItem>
              <CollapsibleTrigger asChild>
                <SidebarMenuButton
                  tooltip={item.name}
                  {...(item.items && item.items.length > 0 ? {} : { asChild: true })}
                >
                  {item.items && item.items.length > 0 ? (
                    <>
                      <item.icon />
                      <span>{item.name}</span>
                      <ChevronRight className="ml-auto transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90" />
                    </>
                  ) : (
                    <Link to={item.url}>
                      <item.icon />
                      <span>{item.name}</span>
                    </Link>
                  )}
                </SidebarMenuButton>
              </CollapsibleTrigger>
              {item.items && item.items.length > 0 && (
                <CollapsibleContent>
                  <SidebarMenuSub>
                    {item.items.map((subItem) => (
                      <NavSubItemWithChildren key={subItem.title} subItem={subItem} />
                    ))}
                  </SidebarMenuSub>
                </CollapsibleContent>
              )}
            </SidebarMenuItem>
          </Collapsible>
        ))}
      </SidebarMenu>
    </SidebarGroup>
  )
}
